import React, { useState, useEffect } from 'react';
import { DateRangePicker } from 'react-bootstrap-daterangepicker';
import moment, { Moment } from 'moment';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { useIntl } from 'react-intl';
import { useLang } from '../../_metronic/i18n/Metronici18n';

interface Props {
  onDateSelect: (start_dt: string, end_dt: string, timezoneOffset: number) => void;
  dateFormat?: string;
  minDate?: Date;
  disabled?: boolean;
}

const DEFAULT_FORMAT = 'MM/DD/yyyy';

type LocaleSettings = {
  format: string;
  separator: string;
  applyLabel: string;
  cancelLabel: string;
  fromLabel: string;
  toLabel: string;
  customRangeLabel: string;
  weekLabel: string;
  daysOfWeek: string[];
  monthNames: string[];
  firstDay: number;
};

const LOCALE_SETTINGS: Record<string, LocaleSettings> = {
  "en": {
    format: DEFAULT_FORMAT,
    separator: " to ",
    applyLabel: "Apply",
    cancelLabel: "Cancel",
    fromLabel: "From",
    toLabel: "To",
    customRangeLabel: "Custom",
    weekLabel: "W",
    daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
    firstDay: 0
  },
  "ar": {
    format: DEFAULT_FORMAT,
    separator: " - ",
    applyLabel: "تطبيق",
    cancelLabel: "إلغاء",
    fromLabel: "من",
    toLabel: "إلى",
    customRangeLabel: "تحديد مخصص",
    weekLabel: "أسبوع",
    daysOfWeek: ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"],
    monthNames: [
      "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
      "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
    ],
    firstDay: 0
  }
};

const BootstrapDateRangePicker: React.FC<Props> = ({ onDateSelect, dateFormat, minDate, disabled = false}) => {
  
  const intl = useIntl();

  const selectedLang = useLang();

  moment.locale(selectedLang);

  const [startDate, setStartDate] = useState(moment().startOf('week').format(DEFAULT_FORMAT));
  const [endDate, setEndDate] = useState(moment().format(DEFAULT_FORMAT));

  const convertArabicToNumeric = (arabicDate:string) => {
    // Define the Arabic to Western numeral mapping
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    const westernNumerals = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    // Replace Arabic numerals with Western numerals in the date string
    for (let i = 0; i < arabicNumerals.length; i++) {
      arabicDate = arabicDate.replace(new RegExp(arabicNumerals[i], 'g'), westernNumerals[i]);
    }
    return arabicDate
  }

  useEffect(() => {
    onDateSelect(convertArabicToNumeric(startDate), convertArabicToNumeric(endDate), new Date().getTimezoneOffset());
  }, [startDate, endDate, onDateSelect]);

  const handleDateRangeChange = (event: any, picker: any): void => {
    const start = picker.startDate.format(DEFAULT_FORMAT);
    const end = picker.endDate.format(DEFAULT_FORMAT);
    setStartDate(start);
    setEndDate(end);

    onDateSelect(convertArabicToNumeric(picker.startDate.format(dateFormat || DEFAULT_FORMAT)), convertArabicToNumeric(picker.endDate.format(dateFormat || DEFAULT_FORMAT)), new Date().getTimezoneOffset());
  };

  const displayDate = () => {
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    } else {
      return intl.formatMessage({ id: 'general.date.select' });
    }
  };

  const minDateMoment: Moment | undefined = minDate ? moment(minDate) : moment(process.env.REACT_APP_STARTED_DT);
  const today = moment();
  const yesterday = moment().subtract(1, 'days');
  const thisWeekStart = moment().startOf('week');
  const thisWeekEnd = moment().endOf('week');
  const lastWeekStart = moment().subtract(1, 'weeks').startOf('week');
  const lastWeekEnd = moment().subtract(1, 'weeks').endOf('week');
  const thisMonthStart = moment().startOf('month');
  const thisMonthEnd = moment().endOf('month');
  const lastMonthStart = moment().subtract(1, 'months').startOf('month');
  const lastMonthEnd = moment().subtract(1, 'months').endOf('month');
  const lifetimeStart = moment(process.env.REACT_APP_STARTED_DT);
  const lifetimeEnd = moment();

  const customRanges = {
    [intl.formatMessage({ id: 'general.date.today' })]: [today, today],
    [intl.formatMessage({ id: 'general.date.yesterday' })]: [yesterday, yesterday],
    [intl.formatMessage({ id: 'general.date.this_week' })]: [thisWeekStart, thisWeekEnd],
    [intl.formatMessage({ id: 'general.date.last_week' })]: [lastWeekStart, lastWeekEnd],
    [intl.formatMessage({ id: 'general.date.this_month' })]: [thisMonthStart, thisMonthEnd],
    [intl.formatMessage({ id: 'general.date.last_month' })]: [lastMonthStart, lastMonthEnd],
    [intl.formatMessage({ id: 'general.date.lifetime' })]: [lifetimeStart, lifetimeEnd],
  };
  
  const localeSettings = LOCALE_SETTINGS[selectedLang]
  
  return (
    <DateRangePicker
      onApply={handleDateRangeChange}
      initialSettings={{
        ranges: customRanges,
        showCustomRangeLabel: true,
        startDate: thisWeekStart,
        endDate: thisWeekEnd,
        minDate: minDateMoment,
        maxDate: lifetimeEnd,
        locale: localeSettings
      }}
    >
      <div className='input-group w-250px border border-gray-300 rounded'>
        <input
          type='text'
          className='form-control border-0'
          value={displayDate()}
          readOnly
          disabled={disabled}
        />
        <div
          className='input-group-text rounded-end border-0'
          data-kt-menu-trigger='click'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='top-end'
        >
          <i className="fa-solid fa-calendar fs-4"></i>
        </div>
      </div>
    </DateRangePicker>
  );
};

export default BootstrapDateRangePicker;